import apiClient from './api';
import { formatContactFullName } from '../utils/contactName';
import type {
  Activity,
  GroupAwareActivityResponse,
  SaveActivityGroupRequest,
  SaveActivityGroupResponse,
} from '../types/activity';
import type { PagedResponse, PageRequest } from '../types/common';

export interface ActivitySearchParams extends PageRequest {
  companyId: number;
  productIds?: number[];
  activityTypeIds?: number[];
  stockIds?: number[];
  clientIds?: number[];
  providerIds?: number[];
  serialNumber?: string;
  fromDate?: string;
  toDate?: string;
  includeArchived?: boolean;
}

// clientName/providerName are FE-only display names synthesized from the first/last/middle
// name parts the backend returns — not sent by the backend itself.
function withDisplayNames(activity: Activity): Activity {
  return {
    ...activity,
    clientName: formatContactFullName({
      firstName: activity.clientFirstName,
      lastName: activity.clientLastName,
      middleName: activity.clientMiddleName,
    }) || undefined,
    providerName: formatContactFullName({
      firstName: activity.providerFirstName,
      lastName: activity.providerLastName,
      middleName: activity.providerMiddleName,
    }) || undefined,
  };
}

function withGroupDisplayNames(item: GroupAwareActivityResponse): GroupAwareActivityResponse {
  return { ...item, activities: item.activities.map(withDisplayNames) };
}

export const activityService = {
  // Search activities with filters and pagination
  searchActivities: async (params: ActivitySearchParams): Promise<PagedResponse<GroupAwareActivityResponse>> => {
    const response = await apiClient.get<PagedResponse<GroupAwareActivityResponse>>('/activities', {
      params: {
        companyId: params.companyId,
        productIds: params.productIds?.join(',') || undefined,
        activityTypeIds: params.activityTypeIds?.join(',') || undefined,
        stockIds: params.stockIds?.join(',') || undefined,
        clientIds: params.clientIds?.join(',') || undefined,
        providerIds: params.providerIds?.join(',') || undefined,
        serialNumber: params.serialNumber || undefined,
        from: params.fromDate,
        to: params.toDate,
        includeArchived: params.includeArchived || undefined,
        page: params.page,
        size: params.size,
        sortBy: params.sortBy,
        sortDir: params.sortDir,
      },
    });
    return { ...response.data, content: response.data.content.map(withGroupDisplayNames) };
  },

  // Get activity by ID
  getActivityById: async (activityId: number): Promise<Activity> => {
    const response = await apiClient.get<Activity>(`/activities/${activityId}`);
    return withDisplayNames(response.data);
  },

  // Universal save: create or update activity group with activities (rid/bid pattern)
  saveActivityGroup: async (request: SaveActivityGroupRequest): Promise<SaveActivityGroupResponse> => {
    const response = await apiClient.post<SaveActivityGroupResponse>('/activities/group', request);
    return response.data;
  },

  // Dry-run validation for universal save. Uses the same BE rules as save,
  // but rolls the transaction back instead of persisting changes.
  validateActivityGroup: async (request: SaveActivityGroupRequest): Promise<void> => {
    await apiClient.post('/activities/group/validate', request);
  },

  // Get a single activity group with all its active activities (for edit pre-population)
  getActivityGroupById: async (groupId: number): Promise<GroupAwareActivityResponse> => {
    const response = await apiClient.get<GroupAwareActivityResponse>(`/activities/group/${groupId}`);
    return withGroupDisplayNames(response.data);
  },

  // Delete an entire activity group (reverses balances, cascade soft-delete)
  deleteActivityGroup: async (groupId: number): Promise<void> => {
    await apiClient.delete(`/activities/group/${groupId}`);
  },

  // Restore a previously archived activity group (reverses balances back, cascade restore)
  restoreActivityGroup: async (groupId: number): Promise<void> => {
    await apiClient.post(`/activities/group/${groupId}/restore`);
  },
};
